import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, ReplaySubject } from 'rxjs';
import { delay, filter, map, timeout } from 'rxjs/operators';
import { environment, OAuthConfig } from '../../environments/environment';

// Isn't it silly to try to make one of these auth services on our own? So, we won't.
// https://github.com/jeroenheijmans/sample-angular-oauth2-oidc-with-auth-guards/blob/master/src/app/core/auth.service.ts
@Injectable({ providedIn: 'root' })
export class CommunityAuthService {

  constructor(
    private _oauthService: OAuthService,
    private router: Router,
  ) {
    // dev only
    // console.log('identities', this.identityClaims);
    // Useful for debugging:
    this._oauthService.events.subscribe(event => {
      if (event instanceof OAuthErrorEvent) {
        console.error(event);
      } else {
        //console.warn(event);
      }
    });

    // This is tricky, as it might cause race conditions (where access_token is set in another
    // tab before everything is said and done there.
    // TODO: Improve this setup.
    window.addEventListener('storage', async (event) => {
      // The `key` is `null` if the event was caused by `.clear()`
      if (event.key !== 'access_token' && event.key !== null) {
        return;
      }

      console.warn('Noticed changes to access_token (most likely from another tab), updating isAuthenticated');
      const isAuthed = this._oauthService.hasValidAccessToken();
      this.isAuthenticatedSubject$.next(isAuthed);
      if (isAuthed) {
        this.loadUserProfile();
      }
      if (!this._oauthService.hasValidAccessToken()) {
        await this.navigateToLoginPage();
      }
    });

    window.addEventListener('storage', (event) => {
      if (event.key !== 'impersonatedHawkId' && event.key !== null) {
        return;
      }

      if (event.newValue) {
        this.isImpersonating = true;
        this.impersonatedHawkId = event.newValue;
      } else {
        this.isImpersonating = false;
        this.impersonatedHawkId = undefined;
      }
    });

    this._oauthService.events
      .subscribe(_ => {
        this.isAuthenticatedSubject$.next(this._oauthService.hasValidAccessToken());
      });

    this._oauthService.events
      .pipe(filter(e => ['token_received'].includes(e.type)))
      .subscribe(this.loadUserProfile.bind(this));
    this._oauthService.events
      .pipe(filter(e => ['token_expires'].includes(e.type)))
      .subscribe(() => this.refresh());
    this._oauthService.events
      .pipe(filter(e => ['token_refreshed'].includes(e.type)))
      .subscribe((t) => console.log('token refreshed', t));
    this._oauthService.events
      .pipe(filter(e => ['token_refresh_error'].includes(e.type)))
      .subscribe((d) => {
        console.error('token refresh error ', d);
        this.isAuthenticatedSubject$.next(false);
        this.logout();
      });
    this._oauthService.events
      .pipe(filter(e => ['session_terminated', 'session_error'].includes(e.type)))
      .subscribe(async e => {
        console.warn('session terminated ', e);
        await this.navigateToLoginPage();
      });

    // this._oauthService.setupAutomaticSilentRefresh();
    this.isAuthenticatedSubject$ = new BehaviorSubject<boolean>(this.isAuthenticated());
    this.isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();
    // this.profile = 
    this.canActivateProtectedRoutes$ = combineLatest([
      this.isAuthenticated$,
      this.isDoneLoading$
    ]).pipe(map(values => values.every(b => b)));

    combineLatest([this.isAuthenticated$, this.isProfileLoaded$, this.isDoneLoading$, this.isImpersonated])
      .subscribe((a) => {
        // Update properties on every event.
        this.isImpersonating = !!sessionStorage[this.impersonationKey];
        this.impersonatedHawkId = this.isImpersonating ? sessionStorage[this.impersonationKey] : null;
        // const claims = this._oauthService?.getIdentityClaims();
        this.actualUsername = this.profile && (<any>this.profile)["uiowahawkid"];
        this.applicationUsername = this.isImpersonating ? this.impersonatedHawkId : this.actualUsername;
        // console.log(`Setting isImpersonating=${this.isImpersonating}, impersonatedHawkId=${this.impersonatedHawkId}, actualUsername=${this.actualUsername}, applicationUsername=${this.applicationUsername}`, this);
      });
  }

  // ITS-added:
  impersonationKey = 'impersonatedHawkId';
  public isImpersonating?: boolean;
  public impersonatedHawkId?: string;
  public applicationUsername?: string;
  public actualUsername?: string;

  // These normally won't be exposed from a service like this, but
  // for debugging it makes sense.
  public get accessToken() { return this._oauthService.getAccessToken(); }
  public get refreshToken() { return this._oauthService.getRefreshToken(); }
  public get identityClaims() { return this._oauthService?.getIdentityClaims(); }
  public get idToken() { return this._oauthService.getIdToken(); }
  public get logoutUrl() { return this._oauthService.logoutUrl; }

  private isAuthenticatedSubject$: BehaviorSubject<boolean>;
  public isAuthenticated$: Observable<boolean>;
  public isAuthenticated() { return this._oauthService.hasValidAccessToken(); }

  private isDoneLoadingSubject$ = new ReplaySubject<boolean>();
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

  private isImpersonatedSubject$ = new BehaviorSubject<boolean>(false);
  public isImpersonated = this.isImpersonatedSubject$.asObservable();

  private isProfileLoadedSubject$ = new BehaviorSubject<boolean>(false);
  public isProfileLoaded$: Observable<boolean> = this.isProfileLoadedSubject$;
  public profile?: object;

  /**
   * Publishes `true` if and only if (a) all the asynchronous initial
   * login calls have completed or errorred, and (b) the user ended up
   * being authenticated.
   *
   * In essence, it combines:
   *
   * - the latest known state of whether the user is authorized
   * - whether the ajax calls for initial log in have all been done
   */
  public canActivateProtectedRoutes$: Observable<boolean>;

  private async loadUserProfile() {
    if (!this.isAuthenticated()) {
      this.isProfileLoadedSubject$.next(false);
      return;
    }
    if (!!sessionStorage["user-profile"]) {
      this.profile = JSON.parse(sessionStorage["user-profile"]);
      this.isProfileLoadedSubject$.next(true);
      return;
    }

    try {
      this.profile = (<any>await this._oauthService.loadUserProfile())?.info;
      sessionStorage["user-profile"] = JSON.stringify(this.profile);
      this.isProfileLoadedSubject$.next(true);
    } catch (err) {
      this.isProfileLoadedSubject$.next(false);
    }
  };

  private async navigateToLoginPage() {
    // TODO: Remember current URL
    await this.router.navigateByUrl('/should-login');
  }

  public async runInitialLoginSequence(loadDiscovery: boolean = true, returnUrl: string = "/"): Promise<boolean> {
    // DEBUGGING
    if (location.hash) {
      console.info(this.runInitialLoginSequence.name, 'Encountered hash fragment, plotting as table');
      console.table(location.hash.substr(1).split('&').map(kvp => kvp.split('=')));
    }

    // STEP 1: get the discovery doc if needed, then check if we are logged in.
    if (loadDiscovery) await this._oauthService.loadDiscoveryDocument();
    await this._oauthService.tryLoginCodeFlow();
    
    // STEP 2: if not logged in, initiate that process with the return URL part of the state param sent to the OAuth authority.
    if (!this._oauthService.hasValidAccessToken()) {
      console.info("No valid access token.");
      this._oauthService.initCodeFlow(returnUrl);
      return false;
    }

    // STEP 3: if logged in, load the profile found at the OAuth spec's userinfo endpoint.
    await this.loadUserProfile();
    this.isDoneLoadingSubject$.next(true);

    // STEP 4: optionally restore original application route from the state parameter that the OAuth authority returns to us full circle.
    if (this._oauthService.state && this._oauthService.state !== 'undefined' && this._oauthService.state !== 'null') {
      let stateUrl = this._oauthService.state;
      if (stateUrl.startsWith('/') === false) {
        stateUrl = decodeURIComponent(stateUrl);
        const url = new URL(stateUrl);
        const path = url.pathname + url.search;
        console.debug(`There was state of ${this._oauthService.state}, so we are sending you to: ${stateUrl}`);
        this.router.navigateByUrl(path, { onSameUrlNavigation: 'reload', });
      }
    }
    return true;
  }

  public login(targetUrl?: string, noState?: boolean) {
    // Note: before version 9.1.0 of the library you needed to
    // call encodeURIComponent on the argument to the method.
    if (!noState) {
      this._oauthService.initCodeFlow(targetUrl || this.router.url);
      return;
    }

    this._oauthService.initCodeFlow();
  }

  public async logout() {
    // Cancel our stuff
    this.endImpersonation();
    
    if (environment.loadDiscoveryDoc || OAuthConfig.revocationEndpoint) {
      await this._oauthService.revokeTokenAndLogout(undefined, true);
    } else {
      console.warn("No revocation endpoint defined; skipping revocation.");
    }
    this._oauthService.logOut();
  }

  public refresh() {
    this._oauthService.refreshToken()
      .catch((e) => {
        if (!this.hasValidToken())
          console.error('refresh error ', e);
      })
      .then((x) => console.log('refresh result ', x))
  }

  public hasValidToken() { return this._oauthService.hasValidAccessToken(); }

  public hawkId?: string;
  public endImpersonation() {
    sessionStorage.removeItem(this.impersonationKey);
    this.isImpersonatedSubject$.next(false);
  }

  public impersonate(hawkId: string) {
    sessionStorage[this.impersonationKey] = hawkId;
    this.isImpersonatedSubject$.next(true);
  }
}
